import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PersonnelCoastGuardPermit, Employee, Branch, Location } from '../../types';
import Input from '../Form/Input';
import Select from '../Form/Select';
import TextArea from '../Form/TextArea';
import Button from '../Form/Button';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { useLocations } from '../../hooks/useLocations';

interface PersonnelPermitFormProps {
  permit?: PersonnelCoastGuardPermit;
  onSubmit: (data: Partial<PersonnelCoastGuardPermit>) => void;
  onCancel: () => void;
}

const PersonnelPermitForm: React.FC<PersonnelPermitFormProps> = ({ permit, onSubmit, onCancel }) => {
  // Fetch employees, branches, and locations from API - NO MOCK DATA
  const { data: apiEmployees = [] } = useEmployees({ populate: '*' });
  const { data: apiBranches = [] } = useBranches({ populate: '*' });
  const { data: apiLocations = [] } = useLocations({ populate: '*' });
  
  const employees = apiEmployees as Employee[];
  const branches = apiBranches as Branch[];
  const locations = apiLocations as Location[];

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<PersonnelCoastGuardPermit>({
    defaultValues: permit || {
      employeeId: '',
      employeeName: '',
      branchId: '',
      branchName: '',
      locationId: '',
      locationAddress: '',
      issueDate: '',
      expiryDate: '',
      status: 'pending',
      notifyDaysBeforeExpiry: 30,
      notes: ''
    }
  });

  useEffect(() => {
    if (permit) {
      reset(permit);
    }
  }, [permit, reset]);

  const selectedEmployeeId = watch('employeeId');
  const selectedBranchId = watch('branchId');
  const selectedLocationId = watch('locationId');

  // Update employee name when employee is selected
  useEffect(() => {
    if (selectedEmployeeId) {
      const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId);
      if (selectedEmployee) {
        reset(prev => ({
          ...prev,
          employeeName: selectedEmployee.name
        }));
      }
    }
  }, [selectedEmployeeId, employees, reset]);

  // Update branch name when branch is selected
  useEffect(() => {
    if (selectedBranchId) {
      const selectedBranch = branches.find(branch => branch.id === selectedBranchId);
      if (selectedBranch) {
        reset(prev => ({
          ...prev,
          branchName: selectedBranch.name
        }));
      }
    }
  }, [selectedBranchId, branches, reset]);

  // Update location address when location is selected
  useEffect(() => {
    if (selectedLocationId) {
      const selectedLocation = locations.find(location => location.id === selectedLocationId);
      if (selectedLocation) {
        reset(prev => ({
          ...prev,
          locationAddress: selectedLocation.address
        }));
      }
    }
  }, [selectedLocationId, locations, reset]);

  const employeeOptions = employees.map(employee => ({
    value: employee.documentId || employee.id,
    label: `${employee.code} - ${employee.name}`
  }));

  const branchOptions = branches
    .filter(branch => branch.status === 'active')
    .map(branch => ({
      value: branch.documentId || branch.id,
      label: branch.name
    }));

  const locationOptions = locations
    .filter(location => location.isActive)
    .map(location => ({
      value: location.id,
      label: location.address
    }));

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'expiring_soon', label: 'Expiring Soon' },
    { value: 'expired', label: 'Expired' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'pending', label: 'Pending' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {permit ? 'Edit Personnel Permit' : 'Add New Personnel Permit'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Employee Selection */}
            <div className="md:col-span-2">
              <Select
                label="Employee *"
                {...register('employeeId', { required: 'Employee is required' })}
                options={employeeOptions}
                error={errors.employeeId?.message}
                placeholder="Select employee..."
              />
            </div>

            {/* Branch */}
            <div>
              <Select
                label="Vessel/Headquarters *"
                {...register('branchId', { required: 'Vessel/Headquarters is required' })}
                options={branchOptions}
                error={errors.branchId?.message}
                placeholder="Select branch..."
              />
            </div>

            {/* Location */}
            <div className="md:col-span-2">
              <Select
                label="Location *"
                {...register('locationId', { required: 'Location is required' })}
                options={locationOptions}
                error={errors.locationId?.message}
                placeholder="Select location..."
              />
            </div>

            {/* Issue Date */}
            <div>
              <Input
                label="Issue Date"
                type="date"
                {...register('issueDate', { required: 'Issue date is required' })}
                error={errors.issueDate?.message}
              />
            </div>

            {/* Expiry Date */}
            <div>
              <Input
                label="Expiry Date"
                type="date"
                {...register('expiryDate', { required: 'Expiry date is required' })}
                error={errors.expiryDate?.message}
              />
            </div>


            {/* Expiry Status */}
            <div>
              <Select
                label="Expiry Status"
                {...register('status', { required: 'Expiry status is required' })}
                options={statusOptions}
                error={errors.status?.message}
              />
            </div>

            {/* Notify Days Before Expiry */}
            <div>
              <Input
                label="Notify Expiring Soon (Days)"
                type="number"
                {...register('notifyDaysBeforeExpiry', { 
                  valueAsNumber: true,
                  min: { value: 1, message: 'Must be at least 1 day' }
                })}
                error={errors.notifyDaysBeforeExpiry?.message}
                placeholder="30"
              />
              <p className="mt-1 text-xs text-gray-500">
                Number of days before expiry to notify as "Expiring Soon"
              </p>
            </div>

            {/* Attachment URL */}
            <div className="md:col-span-2">
              <Input
                label="Attachment URL (Optional)"
                {...register('attachmentUrl')}
                error={errors.attachmentUrl?.message}
                placeholder="Enter document URL or file path"
              />
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <TextArea
                label="Notes (Optional)"
                {...register('notes')}
                error={errors.notes?.message}
                placeholder="Additional notes about this permit..."
                rows={3}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {permit ? 'Update Permit' : 'Add Permit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonnelPermitForm;